import { Dimensions, PixelRatio, Platform } from "react-native";
import * as Device from "expo-device";
import Constants from "expo-constants";
import { AppVersion, ProductVersion, ApiVersion } from "@/versions";

/**
 * Interface representing non-PII user state.
 * Strictly excludes personally identifiable information (no UID, email, or name).
 */
export interface IUserDiagnostics {
  isAuthenticated: boolean;
  isEmailVerified: boolean;
  authProviders: string[];
  isAnonymous: boolean;
}

/**
 * Interface representing device and hardware specs.
 */
export interface IDeviceDiagnostics {
  brand: string;
  manufacturer: string;
  modelName: string;
  deviceType: string;
  isPhysicalDevice: boolean;
  cpuCores: string;
  memory: string;
}

/**
 * Interface representing screen and viewport resolution specs.
 */
export interface IDisplayDiagnostics {
  windowResolution: string;
  screenResolution: string;
  pixelRatio: string;
  fontScale: string;
  orientation: "portrait" | "landscape";
}

/**
 * Interface representing system, OS, and application runtime specs.
 */
export interface ISystemDiagnostics {
  platform: string;
  osName: string;
  osVersion: string;
  appVersion: string;
  productVersion: string;
  apiVersion: string;
  executionEnvironment: string;
  environmentTier: string;
}

/**
 * Interface representing network, locale, and timezone context.
 */
export interface ILocaleDiagnostics {
  locale: string;
  timeZone: string;
  utcTimestamp: string;
  localTimestamp: string;
  networkStatus: string;
  connectionType: string;
  userAgent: string;
}

/**
 * Consolidated feedback diagnostics payload.
 */
export interface IFeedbackDiagnostics {
  userState: IUserDiagnostics;
  device: IDeviceDiagnostics;
  display: IDisplayDiagnostics;
  system: ISystemDiagnostics;
  locale: ILocaleDiagnostics;
}

/**
 * Resolves a human-readable label for DeviceType enum.
 * @param {Device.DeviceType | null} type - Expo DeviceType enum value.
 * @returns {string} Human-readable device category.
 */
export function resolveDeviceType(type?: Device.DeviceType | null): string {
  switch (type) {
    case Device.DeviceType.PHONE:
      return "Phone";
    case Device.DeviceType.TABLET:
      return "Tablet";
    case Device.DeviceType.DESKTOP:
      return "Desktop";
    case Device.DeviceType.TV:
      return "TV";
    default:
      return Platform.OS === "web" ? "Web / Desktop" : "Mobile / Unknown";
  }
}

/**
 * Collects non-PII user state from an active auth user session.
 * Strictly omits UID, email address, and personal name.
 * @param {any} user - Active auth user object.
 * @returns {IUserDiagnostics} Sanitized user state.
 */
export function collectUserState(user?: any): IUserDiagnostics {
  if (!user) {
    return {
      isAuthenticated: false,
      isEmailVerified: false,
      authProviders: [],
      isAnonymous: true,
    };
  }

  const providers = Array.isArray(user.providerData)
    ? user.providerData.map((p: any) => p?.providerId).filter(Boolean)
    : [];

  return {
    isAuthenticated: true,
    isEmailVerified: Boolean(user.emailVerified),
    authProviders: providers.length > 0 ? providers : ["password"],
    isAnonymous: Boolean(user.isAnonymous),
  };
}

/**
 * Collects comprehensive device and hardware diagnostics.
 * @returns {IDeviceDiagnostics} Device hardware telemetry.
 */
export function collectDeviceDiagnostics(): IDeviceDiagnostics {
  const isWeb = Platform.OS === "web";
  const nav = typeof navigator !== "undefined" ? (navigator as any) : null;

  const brand = Device.brand || (isWeb ? "Web Browser" : "Generic");
  const manufacturer = Device.manufacturer || (isWeb ? nav?.vendor || "Web Client" : "Unknown");
  const modelName = Device.modelName || (isWeb ? "Browser Engine" : "Unknown Device");
  const deviceType = resolveDeviceType(Device.deviceType);
  const isPhysicalDevice = Device.isDevice ?? !isWeb;

  const cpuCores = nav?.hardwareConcurrency ? `${nav.hardwareConcurrency} Cores` : "N/A";

  let memory = "N/A";
  if (Device.totalMemory && Device.totalMemory > 0) {
    memory = `${(Device.totalMemory / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  } else if (nav?.deviceMemory) {
    memory = `${nav.deviceMemory} GB`;
  }

  return {
    brand,
    manufacturer,
    modelName,
    deviceType,
    isPhysicalDevice,
    cpuCores,
    memory,
  };
}

/**
 * Collects display and viewport diagnostics.
 * @returns {IDisplayDiagnostics} Display metrics.
 */
export function collectDisplayDiagnostics(): IDisplayDiagnostics {
  const windowDims = Dimensions.get("window");
  const screenDims = Dimensions.get("screen");

  const windowResolution = `${Math.round(windowDims.width)}x${Math.round(windowDims.height)}`;
  const screenResolution = `${Math.round(screenDims.width)}x${Math.round(screenDims.height)}`;
  const pixelRatio = `${PixelRatio.get().toFixed(1)}x`;
  const fontScale = `${PixelRatio.getFontScale().toFixed(2)}x`;
  const orientation = windowDims.width >= windowDims.height ? "landscape" : "portrait";

  return {
    windowResolution,
    screenResolution,
    pixelRatio,
    fontScale,
    orientation,
  };
}

/**
 * Collects system, OS, and application runtime diagnostics.
 * @returns {ISystemDiagnostics} System metrics.
 */
export function collectSystemDiagnostics(): ISystemDiagnostics {
  const osName = Device.osName || Platform.OS;
  const osVersion = Device.osVersion || String(Platform.Version);
  const executionEnvironment = Constants.executionEnvironment || "standard";
  const environmentTier = process.env.EXPO_PUBLIC_ENV || "development";

  return {
    platform: Platform.OS,
    osName,
    osVersion,
    appVersion: AppVersion,
    productVersion: ProductVersion,
    apiVersion: ApiVersion,
    executionEnvironment,
    environmentTier,
  };
}

/**
 * Collects locale, timezone, and connectivity diagnostics.
 * @returns {ILocaleDiagnostics} Locale and network telemetry.
 */
export function collectLocaleDiagnostics(): ILocaleDiagnostics {
  const nav = typeof navigator !== "undefined" ? (navigator as any) : null;

  let locale = "en-US";
  let timeZone = "UTC";
  try {
    const resolved = Intl.DateTimeFormat().resolvedOptions();
    timeZone = resolved.timeZone || "UTC";
    locale = resolved.locale || nav?.language || "en-US";
  } catch {
    locale = nav?.language || "en-US";
  }

  const now = new Date();
  const utcTimestamp = now.toISOString();
  const localTimestamp = now.toLocaleString();

  const networkStatus =
    nav && typeof nav.onLine === "boolean"
      ? nav.onLine
        ? "Connected (Online)"
        : "Disconnected (Offline)"
      : "Connected";

  const connectionType = nav?.connection?.effectiveType ? nav.connection.effectiveType.toUpperCase() : "N/A";
  const userAgent = nav?.userAgent || "Native Mobile Runtime";

  return {
    locale,
    timeZone,
    utcTimestamp,
    localTimestamp,
    networkStatus,
    connectionType,
    userAgent,
  };
}

/**
 * Gathers complete non-PII diagnostics for a feedback submission.
 * @param {any} [user] - Optional active auth user.
 * @returns {IFeedbackDiagnostics} Consolidated diagnostics.
 */
export function collectFeedbackDiagnostics(user?: any): IFeedbackDiagnostics {
  return {
    userState: collectUserState(user),
    device: collectDeviceDiagnostics(),
    display: collectDisplayDiagnostics(),
    system: collectSystemDiagnostics(),
    locale: collectLocaleDiagnostics(),
  };
}

/**
 * Formats diagnostic telemetry into structured markdown for inclusion in the Admin message body.
 * @param {IFeedbackDiagnostics} diag - The diagnostic payload.
 * @returns {string} Formatted markdown sections.
 */
export function formatFeedbackDiagnosticsMarkdown(diag: IFeedbackDiagnostics): string {
  const { userState, device, display, system, locale } = diag;

  return [
    `#### System & Environment Diagnostics`,
    `| Category | Specification | Details |`,
    `| :--- | :--- | :--- |`,
    `| **User State** | Authentication | ${userState.isAuthenticated ? "Authenticated" : "Unauthenticated / Guest"} |`,
    `| | Verified Email | ${userState.isEmailVerified ? "Yes" : "No"} |`,
    `| | Auth Providers | \`${userState.authProviders.join(", ") || "none"}\` |`,
    `| | Anonymous Session | ${userState.isAnonymous ? "Yes" : "No"} |`,
    `| **Device** | Hardware Model | ${device.brand} ${device.modelName} (${device.manufacturer}) |`,
    `| | Device Category | ${device.deviceType} (${device.isPhysicalDevice ? "Physical Device" : "Simulator / Emulator"}) |`,
    `| | CPU & Memory | ${device.cpuCores} • RAM: ${device.memory} |`,
    `| **Display** | Viewport / Screen | Window: ${display.windowResolution} • Screen: ${display.screenResolution} |`,
    `| | Scaling & Density | Ratio: ${display.pixelRatio} • Font Scale: ${display.fontScale} • ${display.orientation} |`,
    `| **OS & Runtime** | Operating System | ${system.osName} (v${system.osVersion}) [${system.platform}] |`,
    `| | App Versions | App: v${system.appVersion} • Product: v${system.productVersion} • API: ${system.apiVersion} |`,
    `| | Environment | Tier: \`${system.environmentTier}\` • Runtime: \`${system.executionEnvironment}\` |`,
    `| **Localization** | Locale & Timezone | ${locale.locale} • Timezone: ${locale.timeZone} |`,
    `| | Timestamps | UTC: \`${locale.utcTimestamp}\` • Local: ${locale.localTimestamp} |`,
    `| | Connectivity | ${locale.networkStatus} (Type: ${locale.connectionType}) |`,
    locale.userAgent !== "Native Mobile Runtime"
      ? `| **User Agent** | Client String | \`${locale.userAgent}\` |`
      : "",
  ]
    .filter(Boolean)
    .join("\n");
}
